import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Star, ChevronRight, Shield, BookOpen } from 'lucide-react';

const MentorshipProgramsOverview = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Mentorship Programs
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              A 12-month formation system designed to raise leaders who carry weight, wisdom, and the presence of God.
            </p>
          </div>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Two Tracks, One Mission
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Both programs run for 12 months and are designed to produce the same core outcomes:
            </p>
          </div>

          {/* Outcomes Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Maturity</h3>
              <p className="text-sm text-gray-600">Spiritual depth and wisdom</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Identity</h3>
              <p className="text-sm text-gray-600">Know who you are in Christ</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Calling</h3>
              <p className="text-sm text-gray-600">Clarity of purpose and assignment</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Formation</h3>
              <p className="text-sm text-gray-600">Character shaped by the Spirit</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Character</h3>
              <p className="text-sm text-gray-600">Integrity and Christlikeness</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Leadership</h3>
              <p className="text-sm text-gray-600">Authority rooted in humility</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Presence</h3>
              <p className="text-sm text-gray-600">Carrying the weight of God</p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Tracks Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Track
            </h2>
            <p className="text-lg text-gray-600">
              Select the program that aligns with your calling and gifting.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Apostolic & Prophetic Track */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-300">
              <div className="h-2 bg-gradient-to-r from-purple-600 to-purple-700"></div>
              
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Track 1</div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Apostolic & Prophetic Leadership
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  For those called to ministry, teaching, intercession, or prophetic leadership.
                </p>
                
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-semibold text-gray-900">12 Months</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      From £120<span className="text-base font-normal text-gray-500">/month</span>
                    </div>
                    <div className="text-xs text-green-600 font-medium">or £1,200 (Save £200)</div>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/mentorship-programs/apostolic-prophetic')}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 group shadow-lg shadow-purple-500/30"
                >
                  <span>View Apostolic & Prophetic Track</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Minstrel & Worship Track */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-300">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600"></div>
              
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-orange-600 uppercase tracking-wider">Track 2</div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Minstrel & Worship Leadership
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  For worship leaders, musicians, singers, and carriers of God's presence.
                </p>
                
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-semibold text-gray-900">12 Months</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      From £120<span className="text-base font-normal text-gray-500">/month</span>
                    </div>
                    <div className="text-xs text-green-600 font-medium">or £1,200 (Save £200)</div>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/mentorship-programs/minstrel-worship')}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 group shadow-lg shadow-orange-500/30"
                >
                  <span>View Minstrel & Worship Track</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentorshipProgramsOverview;